<script setup lang="ts">
  import { useGetAlertsAndBanners } from "@/api/alerts/alerts"
  import { BannerTag } from "@/api/model"
  import { type IllustrationName } from "@/constants/illustrations"
  import nativeHelper from "@/lib/nativeHelper.ts"
  import { useGetAccountId } from "@/lib/useGetAccountId"
  import useLinkResolver from "@/lib/useLinkResolver.ts"
  import TrackingService from "@/services/TrackingService"
  import { whenever } from "@vueuse/core"
  import { computed } from "vue"
  import { useRouter } from "vue-router"
  import { Banner } from "@/components/ui/banner"
  import { Skeleton } from "@/components/ui/skeleton"

  interface MarketingBannersProps {
    bannerTag?: BannerTag
    showSkeleton?: boolean
  }

  const props = withDefaults(defineProps<MarketingBannersProps>(), {
    bannerTag: BannerTag.home,
  })

  const getAccountIdQuery = useGetAccountId()
  const getBannersQuery = useGetAlertsAndBanners(getAccountIdQuery.accountId, {
    bannerTag: props.bannerTag,
  })

  const banner = computed(() => {
    return getBannersQuery.data.value?.marketingBanner
  })

  const isLoading = computed(() => getBannersQuery.isLoading.value)
  const skeletonColor = computed(() =>
    nativeHelper.isNative() ? "medium" : "light",
  )

  const buttonLink = computed(() => banner.value?.buttonLink)
  const router = useRouter()
  const { navigateToUrl } = useLinkResolver(buttonLink, router)

  const onBannerButtonClicked = async (event: MouseEvent) => {
    if (!buttonLink.value) {
      return
    }

    TrackingService.sendEvent("marketing-banner_action-click", {
      entityName: banner.value?.name,
      eventAction: banner.value?.buttonLink,
    })

    await navigateToUrl(event)
  }

  whenever(
    banner,
    (banner) => {
      TrackingService.sendEvent("marketing-banner_impression", {
        entityName: banner.name,
      })
    },
    { once: true },
  )
</script>

<template>
  <div>
    <slot v-if="banner || isLoading" name="header" />
    <Banner
      v-if="banner"
      :label="banner.buttonText"
      :title="banner.title"
      :content="banner.subtitle"
      :illustration="banner.illustrationName as IllustrationName"
      :show-close-button="false"
      @action-button-click="(e) => onBannerButtonClicked(e)"
    />
    <Skeleton
      v-if="!banner && showSkeleton && isLoading"
      class="h-[203px] w-full"
      :color="skeletonColor"
    />
  </div>
</template>
